import Delegate from 'ftdomdelegate';
import { add, remove, qs, qsa, listen } from '@fluorescent/dom';
import { createFocusTrap } from 'focus-trap';

import { getRecentProducts } from '@/lib/recently-viewed';
import { on, getState, hydrate } from '@/glow/events';
import { cart } from '@/glow';
import { makeRequest } from '@/lib/xhr';

function renderRecent(products) {
  return products.length > 0
    ? products.reduce((markup, product) => {
        markup += product.quickCartMarkup;
        return markup;
      }, '')
    : `<p class="quick-cart__empty-state">${general.products.no_recently_viewed}</p>`;
}

export default function QuickCart(node) {
  const overlay = qs('.js-overlay', node);
  const itemsRoot = qs('.js-items', node);
  const footer = qs('.js-footer', node);
  const closeIcon = qs('.js-close', node);
  const loading = itemsRoot.innerHTML;

  const tabLinkCart = qs('.js-tab-link-cart', node);
  const tabLinkRecent = qs('.js-tab-link-recent', node);
  const tabLinkQuickShop = qs('.js-tab-link-quick-shop', node);

  const events = [
    listen(tabLinkCart, 'click', e => e.preventDefault() || viewCart()),
    listen(
      tabLinkRecent,
      'click',
      e => e.preventDefault() || viewRecent(getRecentProducts())
    ),
    listen(
      tabLinkQuickShop,
      'click',
      e => e.preventDefault() || viewQuickAdd()
    ),

    listen(overlay, 'click', () => close()),
    listen(closeIcon, 'click', () => close()),
    listen(node, 'keydown', ({ keyCode }) => {
      if (keyCode === 27) close();
    }),
  ];

  const cartTrap = createFocusTrap(node, { allowOutsideClick: true });

  const delegate = new Delegate(node);

  delegate.on('click', '.js-remove-single', (_, target) => {
    const qty = qs(
      '.js-single-quantity',
      target.closest('.cart__item')
    ).innerHTML;
    cart.updateItem(
      target.closest('.cart__item').dataset.id,
      parseInt(qty) - 1
    );
  });

  delegate.on('click', '.js-add-single', (_, target) => {
    const qty = qs(
      '.js-single-quantity',
      target.closest('.cart__item')
    ).innerHTML;
    cart.updateItem(
      target.closest('.cart__item').dataset.id,
      parseInt(qty) + 1
    );
  });

  // Initial cart fetch
  cart.get().then(cart => {
    hydrate({ cart });
    render(cart);
  });

  const render = cart => {
    add(tabLinkCart, 'active');
    remove(tabLinkQuickShop, 'active');
    remove(tabLinkRecent, 'active');

    renderQuickCart(cart);
    renderFooter();
  };

  const renderQuickCart = cart => {
    const url = `${theme.routes.cart.base}?section_id=quick-cart`;

    makeRequest('GET', url).then(response => {
      itemsRoot.innerHTML = '';
      let container = document.createElement('div');
      container.innerHTML = response;

      const cartItems = qsa('[data-cart-item]', container);

      if (cartItems.length) {
        renderCartItems({
          items: cartItems,
          itemsSorted: cart.sorted,
          highlightFirstProduct: cart.newProductAdded,
        });
      } else {
        itemsRoot.innerHTML = qs(
          '[data-quick-cart-items]',
          container
        ).innerHTML;
      }
    });
  };

  const renderFooter = () => {
    const footerUrl = `${theme.routes.cart.base}?section_id=quick-cart-footer`;

    makeRequest('GET', footerUrl).then(response => {
      let container = document.createElement('div');
      container.innerHTML = response;

      const quickCartFooter = qs('[data-quick-cart-footer]', container);

      if (quickCartFooter) {
        footer.innerHTML = quickCartFooter.innerHTML;
        add(footer, 'active');
      }
    });
  };

  const renderCartItems = options => {
    // cartItems must be an array of objcets to match the id's
    // of the sorted cart items
    const cartItems = options.items.map(item => {
      const itemsObject = {};
      const itemId = qs('.cart__item', item).dataset.id;
      itemsObject.id = parseInt(itemId, 10);
      itemsObject.markup = item;

      return itemsObject;
    });

    // Keep our cart in the correct sort order based on what
    // was most recently added
    const sortedItems = options.itemsSorted.map(
      (
        map => row =>
          cartItems[map.get(row.id)]
      )(new Map(cartItems.map((row, i) => [row.id, i])))
    );

    sortedItems.forEach(item => {
      itemsRoot.insertAdjacentHTML('beforeend', item.markup.innerHTML);
    });

    if (options.highlightFirstProduct) {
      const firstProduct = qs('.cart__item', itemsRoot);

      if (firstProduct) {
        add(firstProduct, 'cart__item--highlight');

        setTimeout(() => {
          remove(firstProduct, 'cart__item--highlight');
        }, 2000);
      }
    }
  };

  const open = (cart, hasquickShopProduct) => {
    add(node, 'is-active');
    cartTrap.activate();

    itemsRoot.innerHTML = loading;

    if (getState().quickShopProduct) {
      add(node, 'has-quick-shop-product');
    }

    setTimeout(() => {
      add(node, 'is-visible');
      if (hasquickShopProduct) {
        add(tabLinkQuickShop, 'active');
        setTimeout(viewQuickAdd(), 10);
      } else {
        add(tabLinkCart, 'active');
        setTimeout(render(cart), 10);
      }
    }, 50);
  };

  const close = () => {
    remove(node, 'is-visible', 'has-quick-shop-product');
    remove(tabLinkQuickShop, 'active');
    remove(tabLinkRecent, 'active');
    remove(tabLinkCart, 'active');
    setTimeout(() => {
      remove(node, 'is-active');
      hydrate({ cartOpen: false });
      hydrate({ quickShopProduct: null });
      cartTrap.deactivate();
    }, 400);
  };

  const viewCart = () => {
    add(tabLinkCart, 'active');
    remove(tabLinkRecent, 'active');
    remove(tabLinkQuickShop, 'active');

    render(getState().cart);
  };

  const viewRecent = products => {
    remove(tabLinkCart, 'active');
    remove(tabLinkQuickShop, 'active');
    add(tabLinkRecent, 'active');
    remove(footer, 'active');
    itemsRoot.innerHTML = renderRecent(products);
  };

  const viewQuickAdd = () => {
    add(tabLinkQuickShop, 'active');
    remove(tabLinkCart, 'active');
    remove(tabLinkRecent, 'active');
    remove(footer, 'active');

    itemsRoot.innerHTML = '';
    itemsRoot.appendChild(getState().quickShopProduct);
  };

  on('cart:toggle', ({ cart, cartOpen, quickShopProduct }) => {
    cartOpen ? open(cart, quickShopProduct) : close();
  });

  on('cart:updated', ({ cartOpen }) => {
    remove(node, 'has-quick-shop-product');

    // Rerender the cart list only if cart is open
    if (cartOpen) {
      render(getState().cart);
    }
  });

  const unload = () => {
    events.forEach(unsubscribe => unsubscribe());
  };

  return { unload };
}
